/*
 *
 *  * (C) Copyright 2006-2020 hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * All Rights Reserved.
 *  *
 *  * These materials are unpublished, proprietary, confidential source code of
 *  * hSenid Mobile Solutions (Pvt) Limited and constitute a TRADE SECRET
 *  * of hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * hSenid Mobile Solutions (Pvt) Limited retains all title to and intellectual
 *  * property rights in these materials.
 *  *
 *
 */

import produce from "immer";

import { call, put, takeLatest } from "redux-saga/effects";
import {
  LoadingStatus,
  usePortalStateSelector,
} from "../../../rootReducer/actions";
import {
  AppCategory,
  getAppCategories,
  getRecommendedApps,
  App,
  RecommendedAppsReq,
  CarouselSlot,
  getAppsBySpecification,
  AppsBySpecificationReq,
  getCarouselImages,
  getAppOfTheWeek,
} from "../../../api/apiCalls";
import { MainPageEvent } from "../../MainUi";

export enum HomePageEvent {
  RESET = "RESET",
  GET_EDITOR_RECOMMENDED_APPS_DATA = "GET_EDITOR_RECOMMENDED_APPS_DATA",
  GET_EDITOR_RECOMMENDED_APPS_DATA_SUCCESS = "GET_EDITOR_RECOMMENDED_APPS_DATA_SUCCESS",
  GET_EDITOR_RECOMMENDED_APPS_DATA_FAILURE = "GET_EDITOR_RECOMMENDED_APPS_DATA_FAILURE",
  GET_APP_CATEGORIES = "GET_APP_CATEGORIES",
  GET_APP_CATEGORIES_SUCCESS = "GET_APP_CATEGORIES_SUCCESS",
  GET_APP_CATEGORIES_FAILURE = "GET_APP_CATEGORIES_FAILURE",
  GET_CAROUSEL_DATA = "GET_CAROUSEL_DATA",
  GET_CAROUSEL_DATA_SUCCESS = "GET_CAROUSEL_DATA_SUCCESS",
  GET_CAROUSEL_DATA_FAILURE = "GET_CAROUSEL_DATA_FAILURE",
  GET_NEW_APPS_DATA = "GET_NEW_APPS_DATA",
  GET_NEW_APPS_DATA_RESULT = "GET_NEW_APPS_DATA_RESULT",
  GET_NEW_APPS_DATA_FAILURE = "GET_NEW_APPS_DATA_FAILURE",
  GET_MOST_USED_APPS_DATA = "GET_MOST_USED_APPS_DATA",
  GET_MOST_USED_APPS_DATA_RESULT = "GET_MOST_USED_APPS_DATA_RESULT",
  GET_MOST_USED_APPS_DATA_FAILURE = "GET_MOST_USED_APPS_DATA_FAILURE",
  GET_TOP_RATED_APPS_DATA = "GET_TOP_RATED_APPS_DATA",
  GET_TOP_RATED_APPS_DATA_RESULT = "GET_TOP_RATED_APPS_DATA_RESULT",
  GET_TOP_RATED_APPS_DATA_FAILURE = "GET_TOP_RATED_APPS_DATA_FAILURE",
  GET_APP_OF_THE_WEEK = "GET_APP_OF_THE_WEEK",
  GET_APP_OF_THE_WEEK_SUCCESS = "GET_APP_OF_THE_WEEK_SUCCESS",
  GET_APP_OF_THE_WEEK_FAILURE = "GET_APP_OF_THE_WEEK_FAILURE",
}

export interface GetRecommendedAppsDataAction {
  type: HomePageEvent.GET_EDITOR_RECOMMENDED_APPS_DATA;
  payload: RecommendedAppsReq;
}

export interface GetRecommendedAppsDataResultsAction {
  type: HomePageEvent.GET_EDITOR_RECOMMENDED_APPS_DATA_SUCCESS;
  results: App[];
}

export interface GetRecommendedAppsDataFailureAction {
  type: HomePageEvent.GET_EDITOR_RECOMMENDED_APPS_DATA_FAILURE;
}

export interface GetAppCategories {
  type: HomePageEvent.GET_APP_CATEGORIES;
}

export interface GetAppCategoriesSuccess {
  type: HomePageEvent.GET_APP_CATEGORIES_SUCCESS;
  results: AppCategory[];
}

export interface GetAppCategoriesFail {
  type: HomePageEvent.GET_APP_CATEGORIES_FAILURE;
}

export interface GetCarouselData {
  type: HomePageEvent.GET_CAROUSEL_DATA;
}

export interface GetCarouselDataSuccess {
  type: HomePageEvent.GET_CAROUSEL_DATA_SUCCESS;
  results: CarouselSlot[];
}

export interface GetCarouselDataFail {
  type: HomePageEvent.GET_CAROUSEL_DATA_FAILURE;
}
export interface GetNewAppsDataAction {
  type: HomePageEvent.GET_NEW_APPS_DATA;
  payload: AppsBySpecificationReq;
}

export interface GetNewAppsDataResultsAction {
  type: HomePageEvent.GET_NEW_APPS_DATA_RESULT;
  results: App[];
}

export interface GetNewAppsDataActionFail {
  type: HomePageEvent.GET_NEW_APPS_DATA_FAILURE;
}

export interface GetMostUsedAppsDataAction {
  type: HomePageEvent.GET_MOST_USED_APPS_DATA;
  payload: AppsBySpecificationReq;
}

export interface GetMostUsedAppsDataResultsAction {
  type: HomePageEvent.GET_MOST_USED_APPS_DATA_RESULT;
  results: App[];
}

export interface GetMostUsedAppsDataActionFail {
  type: HomePageEvent.GET_MOST_USED_APPS_DATA_FAILURE;
}

export interface GetTopRatedAppsDataAction {
  type: HomePageEvent.GET_TOP_RATED_APPS_DATA;
  payload: AppsBySpecificationReq;
}

export interface GetTopRatedAppsDataResultsAction {
  type: HomePageEvent.GET_TOP_RATED_APPS_DATA_RESULT;
  results: App[];
}

export interface GetTopRatedAppsDataActionFail {
  type: HomePageEvent.GET_TOP_RATED_APPS_DATA_FAILURE;
}

export interface GetAppOfTheWeek {
  type: HomePageEvent.GET_APP_OF_THE_WEEK;
}

export interface GetAppOfTheWeekSuccess {
  type: HomePageEvent.GET_APP_OF_THE_WEEK_SUCCESS;
  results: {
    appId: string;
    appName: string;
    appIcon: string;
  };
}

export interface GetAppOfTheWeekFail {
  type: HomePageEvent.GET_APP_OF_THE_WEEK_FAILURE;
}

export type HomePageAction =
  | GetRecommendedAppsDataAction
  | GetRecommendedAppsDataResultsAction
  | GetRecommendedAppsDataFailureAction
  | GetAppCategories
  | GetAppCategoriesSuccess
  | GetAppCategoriesFail
  | GetCarouselData
  | GetCarouselDataSuccess
  | GetCarouselDataFail
  | GetNewAppsDataAction
  | GetNewAppsDataResultsAction
  | GetNewAppsDataActionFail
  | GetMostUsedAppsDataAction
  | GetMostUsedAppsDataResultsAction
  | GetMostUsedAppsDataActionFail
  | GetTopRatedAppsDataAction
  | GetTopRatedAppsDataResultsAction
  | GetTopRatedAppsDataActionFail
  | GetAppOfTheWeek
  | GetAppOfTheWeekFail
  | GetAppOfTheWeekSuccess;

export interface HomePageData {
  loadingStatus: LoadingStatus;
  appOfTheWeek?: {
    appId: string;
    appName: string;
    appIcon: string;
  };
  recommendedApps?: App[];
  newlyAddedApps?: App[];
  mostUsedApps?: App[];
  topRatedApps?: App[];
  categories?: AppCategory[];
  carouselData: CarouselSlot[];
}

const initialState = {
  loadingStatus: LoadingStatus.LOADING_STARTED,
  carouselData: [],
  notification: {
    show: false,
  },
};

/**
 * Reducer
 */
export function homePageReducer(
  state: HomePageData = initialState,
  action: HomePageAction
) {
  return produce(state, (draft) => {
    switch (action.type) {
      case HomePageEvent.GET_EDITOR_RECOMMENDED_APPS_DATA_SUCCESS: {
        draft.loadingStatus = LoadingStatus.LOADING_SUCCESS;
        draft.recommendedApps = action.results;
        break;
      }
      case HomePageEvent.GET_EDITOR_RECOMMENDED_APPS_DATA_FAILURE: {
        draft.loadingStatus = LoadingStatus.LOADING_ERROR;
        break;
      }

      case HomePageEvent.GET_NEW_APPS_DATA: {
        break;
      }

      case HomePageEvent.GET_NEW_APPS_DATA_RESULT: {
        draft.loadingStatus = LoadingStatus.LOADING_SUCCESS;
        draft.newlyAddedApps = action.results.slice(0, 12);
        break;
      }

      case HomePageEvent.GET_NEW_APPS_DATA_FAILURE: {
        draft.loadingStatus = LoadingStatus.LOADING_ERROR;
        break;
      }

      case HomePageEvent.GET_MOST_USED_APPS_DATA: {
        break;
      }

      case HomePageEvent.GET_MOST_USED_APPS_DATA_RESULT: {
        draft.loadingStatus = LoadingStatus.LOADING_SUCCESS;
        draft.mostUsedApps = action.results.slice(0, 6);
        break;
      }

      case HomePageEvent.GET_MOST_USED_APPS_DATA_FAILURE: {
        draft.loadingStatus = LoadingStatus.LOADING_ERROR;
        break;
      }
      case HomePageEvent.GET_TOP_RATED_APPS_DATA: {
        break;
      }

      case HomePageEvent.GET_TOP_RATED_APPS_DATA_RESULT: {
        draft.loadingStatus = LoadingStatus.LOADING_SUCCESS;
        draft.topRatedApps = action.results.slice(0, 6);
        break;
      }
      case HomePageEvent.GET_TOP_RATED_APPS_DATA_FAILURE: {
        draft.loadingStatus = LoadingStatus.LOADING_ERROR;
        break;
      }

      case HomePageEvent.GET_APP_CATEGORIES_SUCCESS: {
        draft.categories = action.results;
        break;
      }
      case HomePageEvent.GET_APP_CATEGORIES_FAILURE: {
        draft.loadingStatus = LoadingStatus.LOADING_ERROR;
        break;
      }
      case HomePageEvent.GET_CAROUSEL_DATA_SUCCESS: {
        draft.carouselData = action.results;
        break;
      }
      case HomePageEvent.GET_CAROUSEL_DATA_FAILURE: {
        draft.loadingStatus = LoadingStatus.LOADING_ERROR;
        break;
      }
      case HomePageEvent.GET_APP_OF_THE_WEEK_SUCCESS: {
        draft.appOfTheWeek = action.results;

        break;
      }
      case HomePageEvent.GET_APP_OF_THE_WEEK_FAILURE: {
        draft.loadingStatus = LoadingStatus.LOADING_ERROR;
        break;
      }
    }
  });
}

/**
 * Selector
 */
export function useHomePageDataSelector(): HomePageData {
  return usePortalStateSelector<HomePageData>((ev) => ev.homePageData);
}

/**
 * Page Saga
 */
export const homePageSagas = [
  geHomePageDataWatcherSaga,
  getAppCategoriesWatcherSaga,
  carouelImagesWatcherSaga,
];

/**
 * Watcher Sagas
 */
function* geHomePageDataWatcherSaga() {
  yield takeLatest(
    HomePageEvent.GET_EDITOR_RECOMMENDED_APPS_DATA,
    callGetRecommendedAppsData
  );
  yield takeLatest(HomePageEvent.GET_NEW_APPS_DATA, callGetNewlyAddedAppsData);
  yield takeLatest(
    HomePageEvent.GET_MOST_USED_APPS_DATA,
    callGetMostUsedAppsData
  );
  yield takeLatest(
    HomePageEvent.GET_TOP_RATED_APPS_DATA,
    callGetTopRatedAppsData
  );
  yield takeLatest(HomePageEvent.GET_APP_OF_THE_WEEK, callGetAppOfTheWeek);
}

function* getAppCategoriesWatcherSaga() {
  yield takeLatest(HomePageEvent.GET_APP_CATEGORIES, callGetAppCategories);
}

function* carouelImagesWatcherSaga() {
  yield takeLatest(HomePageEvent.GET_CAROUSEL_DATA, callGetCarouselImages);
}

/**
 * Handler functions
 */

const arr = ["E1008", "E1009", "E1010", "E1011", "E1012", "E1013"];

function* callGetTopRatedAppsData(action: GetTopRatedAppsDataAction) {
  try {
    const { payload, statusCode } = yield call(
      getAppsBySpecification,
      action.payload
    );

    if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      // window.location.reload();
    } else {
      yield put({
        type: HomePageEvent.GET_TOP_RATED_APPS_DATA_RESULT,
        results: payload,
      });
    }
  } catch (e) {
    yield put({ type: HomePageEvent.GET_TOP_RATED_APPS_DATA_FAILURE });
    console.error(e);
  }
}

function* callGetMostUsedAppsData(action: GetMostUsedAppsDataAction) {
  try {
    const { payload, statusCode } = yield call(
      getAppsBySpecification,
      action.payload
    );

    if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      // window.location.reload();
    } else {
      yield put({
        type: HomePageEvent.GET_MOST_USED_APPS_DATA_RESULT,
        results: payload,
      });
    }
  } catch (e) {
    yield put({ type: HomePageEvent.GET_MOST_USED_APPS_DATA_FAILURE });
    console.error(e);
  }
}

function* callGetNewlyAddedAppsData(action: GetNewAppsDataAction) {
  try {
    const { payload, statusCode } = yield call(
      getAppsBySpecification,
      action.payload
    );
    if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      // window.location.reload();
    } else {
      yield put({
        type: HomePageEvent.GET_NEW_APPS_DATA_RESULT,
        results: payload,
      });
    }
  } catch (e) {
    yield put({ type: HomePageEvent.GET_NEW_APPS_DATA_FAILURE });
    console.error(e);
  }
}

function* callGetRecommendedAppsData(action: GetRecommendedAppsDataAction) {
  try {
    const { payload, statusCode } = yield call(
      getRecommendedApps,
      action.payload
    );
    if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      window.location.reload();
    } else {
      yield put({
        type: HomePageEvent.GET_EDITOR_RECOMMENDED_APPS_DATA_SUCCESS,
        results: payload,
      });
    }
  } catch (e) {
    yield put({
      type: HomePageEvent.GET_EDITOR_RECOMMENDED_APPS_DATA_FAILURE,
    });
    console.error(e);
  }
}

function* callGetAppCategories(action: GetAppCategories) {
  try {
    const { payload } = yield call(getAppCategories);
    yield put({
      type: HomePageEvent.GET_APP_CATEGORIES_SUCCESS,
      results: payload,
    });
  } catch (e) {
    yield put({ type: HomePageEvent.GET_APP_CATEGORIES_FAILURE });
    console.error(e);
  }
}

function* callGetCarouselImages(action: GetCarouselData) {
  try {
    const { payload, statusCode } = yield call(getCarouselImages);
    if (statusCode === "S1000") {
      yield put({
        type: HomePageEvent.GET_CAROUSEL_DATA_SUCCESS,
        results: payload,
      });
    } else if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      window.location.reload();
    } else {
      yield put({
        type: HomePageEvent.GET_CAROUSEL_DATA_FAILURE,
      });
    }
  } catch (e) {
    console.error(e);
    yield put({
      type: HomePageEvent.GET_CAROUSEL_DATA_FAILURE,
    });
  }
}

function* callGetAppOfTheWeek(action: GetAppOfTheWeek) {
  try {
    const { payload, statusCode } = yield call(getAppOfTheWeek);
    if (statusCode === "S1000") {
      yield put({
        type: HomePageEvent.GET_APP_OF_THE_WEEK_SUCCESS,
        results: payload,
      });
    }
  } catch (e) {
    console.error(e);
    yield put({
      type: HomePageEvent.GET_APP_OF_THE_WEEK_FAILURE,
    });
  }
}
